import React, { useEffect, useMemo, useRef, useState } from "react";
import i18next from "i18next";
import { CategoryPickerCategory, CategoryPickerComponent, CategoryPickerSubcategory } from "@me-pos/category-picker";

import { PartialMenuCategory } from "../../../interfaces/meMenuInterfaces/interfaces";
import TranslationMissingDot from "../components/LanguageVersionSelect/TranslationMissingDot";
import { EditMenuService } from "../../MenuEditContentPage/service/editMenuService";
import { MenuCategorySelection } from "./helper/menuCategoryFilterHelper";

interface MenuCategoryFilterPickerProps {
    categories: PartialMenuCategory[] | null | undefined;
    value: MenuCategorySelection;
    onChange: (value: MenuCategorySelection) => void;
    placeholder: string;
    inputId: string;
    isTranslationMode: boolean;
    translationTooltip: string;
    disabled?: boolean;
}

const MenuCategoryFilterPicker: React.FC<MenuCategoryFilterPickerProps> = ({
    categories,
    value,
    onChange,
    placeholder,
    inputId,
    isTranslationMode,
    translationTooltip,
    disabled = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef<HTMLDivElement | null>(null);
    const pickerRef = useRef<HTMLDivElement | null>(null);

    const serviceCategories = useMemo(() => {
        if (!categories?.length) {
            return null;
        }

        return EditMenuService.categoryModuleCreator(
            categories,
            value.currentCategoryId,
            value.currentSubcategoryId,
            isTranslationMode
        );
    }, [categories, isTranslationMode, value.currentCategoryId, value.currentSubcategoryId]);

    const selectedCategoryLabel = useMemo(() => {
        if (!serviceCategories || !value.currentCategoryId) {
            return "";
        }

        const selectedCategory = serviceCategories.find((category) => category.id === value.currentCategoryId);
        if (!selectedCategory) {
            return "";
        }

        if (value.currentSubcategoryId) {
            const selectedSubcategory = selectedCategory.subcategoryList?.find(
                (subcategory) => subcategory.id === value.currentSubcategoryId
            );

            if (selectedSubcategory) {
                return selectedSubcategory.name;
            }
        }

        return selectedCategory.name;
    }, [serviceCategories, value.currentCategoryId, value.currentSubcategoryId]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const isClickInsideTrigger = triggerRef.current?.contains(target) ?? false;
            const isClickInsidePicker = pickerRef.current?.contains(target) ?? false;

            if (!isClickInsideTrigger && !isClickInsidePicker) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    useEffect(() => {
        if (disabled) {
            setIsOpen(false);
        }
    }, [disabled]);

    const handleCategoryChange = (currentCategory: CategoryPickerCategory) => {
        if (currentCategory.isPicked) {
            onChange({
                currentCategoryId: currentCategory.id,
                currentSubcategoryId: null,
            });
        } else {
            onChange({
                currentCategoryId: null,
                currentSubcategoryId: null,
            });
        }

        setIsOpen(false);
    };

    const handleSubcategoryChange = (
        pickedSubcategory: CategoryPickerSubcategory,
        currentCategory: CategoryPickerCategory
    ) => {
        if (pickedSubcategory.isPicked) {
            onChange({
                currentCategoryId: currentCategory.id,
                currentSubcategoryId: pickedSubcategory.id,
            });
        } else {
            onChange({
                currentCategoryId: null,
                currentSubcategoryId: null,
            });
        }

        setIsOpen(false);
    };

    const chevronIconClassName = isOpen ? "bi-chevron-up" : "bi-chevron-down";

    return (
        <div className="position-relative">
            <div
                id={inputId}
                ref={triggerRef}
                className={`form-control d-flex align-items-center justify-content-between ${
                    disabled ? "bg-light cursor-not-allowed" : ""
                }`}
                role="button"
                tabIndex={disabled ? -1 : 0}
                onClick={() => {
                    if (!disabled) {
                        setIsOpen((prev) => !prev);
                    }
                }}
                onKeyDown={(event) => {
                    if (disabled) {
                        return;
                    }

                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setIsOpen((prev) => !prev);
                    }

                    if (event.key === "Escape") {
                        setIsOpen(false);
                    }
                }}
            >
                <span className={selectedCategoryLabel ? "" : "text-grey"}>{selectedCategoryLabel || placeholder}</span>
                <i className={`bi ${chevronIconClassName} me-2`} />
            </div>

            {isOpen && serviceCategories ? (
                <div
                    ref={pickerRef}
                    className="dropdown position-absolute p-1 opacity-1 z-2 top-10 start-0 end-0 mt-1 bg-white shadow overflow-hidden"
                    style={{ maxHeight: "320px" }}
                >
                    <CategoryPickerComponent
                        i18n={i18next}
                        serviceCategories={serviceCategories}
                        showMissingTranslationDot={isTranslationMode}
                        renderMissingTranslationDot={() => <TranslationMissingDot tooltipText={translationTooltip} />}
                        onSearchChange={() => {}}
                        onChangeCategory={handleCategoryChange}
                        onChangeSubcategory={handleSubcategoryChange}
                        isOpen
                    />
                </div>
            ) : null}
        </div>
    );
};

export default MenuCategoryFilterPicker;
